import {
  applyActionCode,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getIdTokenResult,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  updatePassword,
  type Auth,
  type User,
} from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
  type Firestore,
} from 'firebase/firestore';
import type {
  AuthIdentity,
  AuthListener,
  AuthRepository,
  ChangePasswordInput,
  RegisterInput,
  Unsubscribe,
  AppRole,
  PartnerRole,
} from '@nx/domain';
import { AuthError } from '@nx/shared';
import { mapFirebaseError } from './mapFirebaseError';
import { FirebaseUserRepository, USERS_COLLECTION } from './FirebaseUserRepository';

const COMPANIES_COLLECTION = 'companies';

function getProviderFromUser(user: User): 'password' | 'google' {
  return user.providerData[0]?.providerId === 'google.com' ? 'google' : 'password';
}

export interface FirebaseAuthDeps {
  auth: Auth;
  db: Firestore;
}

/**
 * Firebase implementation of AuthRepository (Infrastructure layer).
 * Translates Firebase Auth + Firestore into the Domain shape AuthIdentity.
 */
export class FirebaseAuthRepository implements AuthRepository {
  private readonly userRepo: FirebaseUserRepository;

  constructor(private readonly deps: FirebaseAuthDeps) {
    this.userRepo = new FirebaseUserRepository(deps.db);
  }

  async loginWithPassword(email: string, password: string): Promise<AuthIdentity> {
    if (!email || !password) {
      throw new AuthError('auth/invalid-credentials', 'Please enter your email and password.');
    }
    try {
      const cred = await signInWithEmailAndPassword(this.deps.auth, email, password);
      return await this.buildIdentity(cred.user);
    } catch (error) {
      throw mapFirebaseError(error);
    }
  }

  async registerCustomerOrPartner(input: RegisterInput): Promise<AuthIdentity> {
    let createdUser: User | null = null;
    try {
      const { user } = await createUserWithEmailAndPassword(
        this.deps.auth,
        input.email,
        input.password,
      );
      createdUser = user;

      const companyRef = doc(collection(this.deps.db, COMPANIES_COLLECTION));
      const companyId = companyRef.id;
      const userRef = doc(this.deps.db, USERS_COLLECTION, user.uid);

      const batch = writeBatch(this.deps.db);
      batch.set(companyRef, {
        companyName: input.company.companyName.trim(),
        role: input.company.partnerRole,
        companyCode: companyId,
        accountStatus: 'enabled',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      batch.set(userRef, {
        uid: user.uid,
        email: input.email,
        provider: 'password',
        appRole: 'user' satisfies NonNullable<AppRole>,
        displayName: input.user.fullName.trim(),
        phone: input.user.phone.trim(),
        companyCode: companyId,
        companyName: input.company.companyName.trim(),
        userStatus: 'enabled',
        mustChangePassword: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await batch.commit();

      try {
        await sendEmailVerification(user);
      } catch {
        // Non-fatal: user can resend from the verify-email page.
      }

      return await this.buildIdentity(user, {
        appRole: 'user',
        partnerRole: input.company.partnerRole,
        companyCode: companyId,
        companyName: input.company.companyName.trim(),
        displayName: input.user.fullName.trim(),
        mustChangePassword: false,
      });
    } catch (error) {
      if (createdUser) {
        try {
          await createdUser.delete();
        } catch {
          // Best-effort rollback when Firestore profile creation fails.
        }
      }
      throw mapFirebaseError(error);
    }
  }

  async changePassword(input: ChangePasswordInput): Promise<void> {
    const user = this.deps.auth.currentUser;
    if (!user || !user.email) {
      throw new AuthError('auth/invalid-credentials', 'You must be signed in to change your password.');
    }
    try {
      const credential = EmailAuthProvider.credential(user.email, input.currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, input.newPassword);
      await this.userRepo.setMustChangePassword(user.uid, false);
    } catch (error) {
      throw mapFirebaseError(error);
    }
  }

  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(this.deps.auth);
    } catch (error) {
      throw mapFirebaseError(error);
    }
  }

  watchAuthState(listener: AuthListener): Unsubscribe {
    const unsubscribe = onAuthStateChanged(
      this.deps.auth,
      async (user) => {
        if (!user) {
          listener(null);
          return;
        }
        try {
          const identity = await this.buildIdentity(user);
          listener(identity);
        } catch (error) {
          console.error('[FirebaseAuthRepository] watchAuthState identity build failed', error);
          listener(null);
        }
      },
      (error) => {
        console.error('[FirebaseAuthRepository] watchAuthState error', error);
        listener(null);
      },
    );
    return () => unsubscribe();
  }

  async getCurrentIdentity(): Promise<AuthIdentity | null> {
    const user = this.deps.auth.currentUser;
    if (!user) return null;
    return await this.buildIdentity(user);
  }

  async sendVerificationEmail(): Promise<void> {
    const user = this.deps.auth.currentUser;
    if (!user) throw new AuthError('auth/invalid-credentials', 'No signed-in user.');
    try {
      await sendEmailVerification(user);
    } catch (error) {
      throw mapFirebaseError(error);
    }
  }

  async reloadCurrentUser(): Promise<void> {
    const user = this.deps.auth.currentUser;
    if (!user) throw new AuthError('auth/invalid-credentials', 'No signed-in user.');
    await user.reload();
  }

  async applyVerificationCode(oobCode: string): Promise<void> {
    try {
      await applyActionCode(this.deps.auth, oobCode);
    } catch (error) {
      throw mapFirebaseError(error);
    }
  }

  /** Build the domain AuthIdentity from a Firebase user, claims, and Firestore profile. */
  private async buildIdentity(
    user: User,
    overrides: Partial<AuthIdentity> = {},
  ): Promise<AuthIdentity> {
    const profile = await this.userRepo.findById(user.uid);
    let appRole: AppRole = profile?.appRole ?? null;
    let partnerRole: PartnerRole | null = null;

    try {
      const tokenResult = await getIdTokenResult(user);
      const claims = tokenResult.claims as Record<string, unknown>;
      if (typeof claims.appRole === 'string') {
        appRole = claims.appRole as AppRole;
      }
      if (typeof claims.partnerRole === 'string') {
        partnerRole = claims.partnerRole as PartnerRole;
      }
    } catch {
      // Ignore claim lookup failures; fall back to profile.
    }

    if (!appRole) {
      appRole = 'user';
    }

    if (!partnerRole && profile?.companyCode) {
      try {
        const companySnap = await getDoc(
          doc(this.deps.db, COMPANIES_COLLECTION, profile.companyCode),
        );
        if (companySnap.exists()) {
          const companyRole = companySnap.data()?.role;
          if (typeof companyRole === 'string') {
            partnerRole = companyRole as PartnerRole;
          }
        }
      } catch {
        // Fall back without partner role.
      }
    }

    const identity: AuthIdentity = {
      userId: user.uid,
      email: user.email ?? profile?.email ?? null,
      emailVerified: user.emailVerified,
      displayName: profile?.displayName ?? user.displayName ?? null,
      photoURL: profile?.photoURL ?? user.photoURL ?? null,
      provider: profile?.provider ?? getProviderFromUser(user),
      appRole,
      partnerRole,
      companyCode: profile?.companyCode ?? null,
      companyName: profile?.companyName ?? null,
      mustChangePassword: profile?.mustChangePassword ?? false,
      ...overrides,
    };
    return identity;
  }
}
